import axios from 'axios';
import { IS_INDIA } from '../config/region';
import {
  AuthResponse,
  User,
  SignupForm,
  LoginForm,
  PasswordResetForm,
  ApiResponse,
  InboundCall,
  InboundCallStats,
  InboundCallMatchingRule,
  InboundCallCreate,
  InboundCallMatchingRuleCreate,
  Workspace,
  WorkspaceMember,
  WorkspaceInvite,
  WorkspaceRole,
  InviteLookupResponse,
  InviteAcceptRequest,
  InviteAcceptResponse,
} from '../types';
import {
  buildStartConversationPayload,
  buildPersistentWebCallPayload,
  buildSubscribePayload,
  buildAddCreditsPayload,
  buildCreateCustomerPayload,
} from './payload';

// REACT_APP_API_URL = absolute origin of the regional backend
// (e.g. https://cold-caller-backend-uat-...run.app). The React app at
// au.agentnoda.ai / in.agentnoda.ai (Cloud Run domain mappings, LT-487
// / LT-490 / LT-491) makes cross-origin calls to its regional backend —
// CORS is configured on the backend to allow the frontend's origin.
//
// Previously (LT-489) this was changed to "" / relative-origin under the
// assumption nginx in the frontend container would proxy /api/*, /auth/*,
// /billing/*, etc. to the backend. That doesn't work cleanly: the backend
// has 35+ top-level router prefixes that overlap with React Router routes
// (/agents, /campaigns, /contacts, /settings, /admin, ...), so nginx
// can't disambiguate "is this a React route or a backend route" without
// a full /api/-prefix refactor of every backend router + every axios
// call site. That refactor is out of scope for LT-487; reverting to
// absolute URLs is the operational answer.
const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error(
    'REACT_APP_API_URL environment variable is not set. ' +
    'Set it to the absolute origin of the regional backend ' +
    '(e.g. https://cold-caller-backend-uat-XXX.run.app for AU UAT).'
  );
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30s — prevents indefinite hangs on slow/dead endpoints
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle authentication errors - clear token and notify app so user is redirected to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normalize non-string FastAPI/Pydantic error payloads (e.g. 422 detail arrays)
    // so UI state/render paths never receive objects as React children.
    // LT-126+ uses a structured {code, message} detail shape — preserve it so
    // callers can branch on code, and let extractErrorMessage handle the read.
    try {
      if (error?.response?.data && typeof error.response.data === 'object') {
        const detail = error.response.data.detail;
        const isStructuredCode =
          detail && typeof detail === 'object' && !Array.isArray(detail) && typeof (detail as any).code === 'string';
        if (detail != null && typeof detail !== 'string' && !isStructuredCode) {
          error.response.data.detail = extractErrorMessage(error, 'Something went wrong. Please try again.');
        }
      }
    } catch {
      // Best-effort normalization only.
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const status = error.response?.status;
    if (status && status >= 500) {
      import('../utils/analytics').then(({ trackEvent }) => {
        import('../utils/analyticsEvents').then(({ AnalyticsEvents }) => {
          trackEvent(AnalyticsEvents.API_ERROR_CRITICAL, {
            status,
            url: error.config?.url,
            method: error.config?.method,
          });
        });
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Extract a human-readable error string from an Axios error.
 * FastAPI returns `detail` as a string for most errors, but as an array of
 * objects (Pydantic validation) for 422 responses.  Passing that array into
 * React state and rendering it with `{error}` causes the "Objects are not
 * valid as a React child" crash.  This helper always returns a string.
 */
export function extractErrorMessage(err: any, fallback = 'Something went wrong. Please try again.'): string {
  const raw = err?.response?.data?.detail;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && typeof (raw as any).message === 'string') {
    return (raw as any).message;
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return String(first.msg).replace(/^Value error, ?/i, '');
  }
  // Translate axios transport-level failures (no HTTP response body) into
  // operator-readable copy. Without this the user sees the raw axios string
  // "timeout of 30000ms exceeded" — e.g. when the "Call lead" dispatch waits
  // on a slow Voice Gateway and the 30s client timeout fires before the
  // backend responds. Match by code first, then message as a fallback since
  // some environments surface the timeout only via the message text.
  const code = err?.code;
  if (
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    /timeout of \d+ms exceeded/i.test(err?.message || '')
  ) {
    return 'The request timed out before the server responded. Please check your connection and try again.';
  }
  if (code === 'ERR_NETWORK' || err?.message === 'Network Error') {
    return 'Network error — could not reach the server. Please check your connection and try again.';
  }
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
}

export const authAPI = {
  signup: async (userData: SignupForm): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/signup', userData);
    return response.data;
  },

  login: async (credentials: LoginForm): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  resetPasswordRequest: async (data: PasswordResetForm): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/auth/reset-password-request', data);
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  updateProfile: async (data: { first_name?: string; last_name?: string; phone?: string }): Promise<User> => {
    const response = await api.patch<User>('/auth/me', data);
    return response.data;
  },

  /** Mark onboarding tour as finished (completed or skipped). Persists in DB so tour shows only once per user. */
  setTourFinished: async (): Promise<User> => {
    const response = await api.post<User>('/auth/me/tour-finished');
    return response.data;
  },

  checkEmail: async (email: string): Promise<{ available: boolean }> => {
    const response = await api.get<{ available: boolean }>(`/auth/check-email?email=${encodeURIComponent(email)}`);
    return response.data;
  },
};

export const assistantsAPI = {
  getAssistants: async () => {
    const response = await api.get('/assistants/');
    return response.data;
  },

  getAssistant: async (assistantId: string) => {
    const response = await api.get(`/assistants/${assistantId}`);
    return response.data;
  },
};

// LT-611 — public "Get called by our AI now" demo widget (GTM §7).
// Unauthenticated landing-page widget. These call the public /demo/* endpoints;
// an anonymous visitor carries no token so the request interceptor adds no auth
// header (and the endpoints ignore one if a logged-in visitor is present).
export interface DemoContext {
  key: string;
  label: string;
  category: string;
  description: string;
}

export interface DemoContextsResponse {
  enabled: boolean;
  contexts: DemoContext[];
  max_duration_seconds: number;
  freetext_max_chars: number;
  region: string;
  phone_example?: string;
}

export const demoAPI = {
  getContexts: async (): Promise<DemoContextsResponse> => {
    const response = await api.get('/demo/contexts');
    return response.data;
  },
  startWebCall: async (payload: { preset_key?: string | null; context_text?: string | null }): Promise<{
    success: boolean;
    demo_call_id?: string;
    session_id?: string;
    livekit_url?: string;
    participant_token?: string;
    max_duration_seconds?: number;
    voice_id?: string | null;
  }> => {
    const response = await api.post('/demo/call/web', payload);
    return response.data;
  },
  endWebCall: async (sessionId: string, durationSeconds: number): Promise<{
    success: boolean;
    credits_charged: number;
  }> => {
    const response = await api.post('/demo/call/web/end', {
      session_id: sessionId,
      duration_seconds: durationSeconds,
    });
    return response.data;
  },
  startPhoneCall: async (payload: {
    phone: string;
    consent: boolean;
    preset_key?: string | null;
    context_text?: string | null;
  }): Promise<{ success: boolean; demo_call_id?: string; message: string }> => {
    const response = await api.post('/demo/call/phone', payload);
    return response.data;
  },
};

export const conversationAPI = {
  startConversation: async (assistantId?: string, customerId?: string) => {
    const response = await api.post('/conversation/start', buildStartConversationPayload(assistantId, customerId));
    return response.data;
  },

  createPersistentWebCall: async (assistantId?: string, customerId?: string) => {
    const response = await api.post('/conversation/create-persistent', buildPersistentWebCallPayload(assistantId, customerId));
    return response.data;
  },

  getCallStatus: async (callId: string) => {
    const response = await api.get(`/conversation/status/${callId}`);
    return response.data;
  },

  endCall: async (callId: string) => {
    const response = await api.delete(`/conversation/end/${callId}`);
    return response.data;
  },

  getWebCallUrl: async (callId: string) => {
    const response = await api.get(`/conversation/status/${callId}`);
    return response.data.details?.webCallUrl || null;
  },
  testPrompt: async (testPromptData: {
    // Legacy SavedPrompt fields — the Speak-to-an-Agent / NewRun path.
    agent_name?: string;
    context?: string;
    goals?: string;
    target_audience?: string;
    call_objective?: string;
    success_metrics?: string;
    custom_fields?: Array<{ title: string; value: string }>;
    voice_id?: string;
    phone?: string;
    // LT-459: active Campaign id. When set the backend resolves the L1
    // agent from the campaign's wrapped sequence and composes the L1-L5
    // prompt (campaign supplies the L2 brief), ignoring the legacy fields
    // above. Used by the Contacts "Call lead" modal; requires contact_id.
    campaign_id?: string;
    // LT-256 follow-up: pass contact_id for Contact-page ad-hoc calls
    // so the gateway webhook can link the resulting CallSummary +
    // Activity back to the contact (call history + transcript).
    // Speak-to-an-Agent flows (Create Agent page) leave this unset.
    contact_id?: string;
  }) => {
    const response = await api.post('/conversation/test-prompt', testPromptData);
    return response.data;
  },
  getTestCallStatus: async (callId: string): Promise<{ status: string; ended: boolean }> => {
    const response = await api.get(`/conversation/test-prompt/${callId}/status`);
    return response.data;
  },
  endTestPromptCall: async (durationSeconds: number, callId?: string) => {
    const response = await api.post('/conversation/test-prompt/end', {
      duration_seconds: durationSeconds,
      call_id: callId
    });
    return response.data;
  },
  // LT-520 — Campaign-level Test Agent (WebRTC). Backend composes the
  // campaign's L1+L2 prompt against a synthetic Contact and returns a
  // LiveKit token + URL the browser uses to join the room directly.
  testPromptWeb: async (payload: { campaign_id: string }): Promise<{
    success: boolean;
    session_id?: string;
    livekit_url?: string;
    participant_token?: string;
    max_duration_seconds?: number;
    voice_id?: string | null;
    message?: string;
    error?: string;
  }> => {
    const response = await api.post('/conversation/test-prompt/web', payload);
    return response.data;
  },
  endTestPromptWebCall: async (
    sessionId: string,
    durationSeconds: number,
  ): Promise<{
    success: boolean;
    credits_deducted: number;
    message: string;
    error?: string;
  }> => {
    const response = await api.post('/conversation/test-prompt/web/end', {
      session_id: sessionId,
      duration_seconds: durationSeconds,
    });
    return response.data;
  },
  savePrompt: async (promptData: {
    name: string;
    agent_name?: string;
    context?: string;
    goals?: string;
    target_audience?: string;
    call_objective?: string;
    success_metrics?: string;
    custom_fields?: Array<{ title: string; value: string }>;
    voice_id?: string;
  }) => {
    const response = await api.post('/conversation/saved-agents', promptData);
    return response.data;
  },
  getSavedPrompts: async () => {
    const response = await api.get('/conversation/saved-agents');
    return response.data;
  },
  getSavedPrompt: async (promptId: string) => {
    const response = await api.get(`/conversation/saved-agents/${promptId}`);
    return response.data;
  },
  deleteSavedPrompt: async (promptId: string) => {
    const response = await api.delete(`/conversation/saved-agents/${promptId}`);
    return response.data;
  },
  updateSavedPrompt: async (promptId: string, promptData: {
    name: string;
    agent_name?: string;
    context?: string;
    goals?: string;
    target_audience?: string;
    call_objective?: string;
    success_metrics?: string;
    custom_fields?: Array<{ title: string; value: string }>;
    voice_id?: string;
  }) => {
    const response = await api.put(`/conversation/saved-agents/${promptId}`, promptData);
    return response.data;
  },
};

export const callsAPI = {
  /** Create an outbound phone call (uses user's or global outbound assistant + phone number ID). */
  createOutboundCall: async (params: {
    assistantId: string;
    assistantOverrides?: Record<string, unknown>;
    phoneNumber: string;
  }) => {
    const normalized = params.phoneNumber.trim().startsWith('+')
      ? params.phoneNumber.trim()
      : `+${params.phoneNumber.trim()}`;
    const body: Record<string, unknown> = {
      assistantId: params.assistantId,
      phoneNumber: { number: normalized },
    };
    if (params.assistantOverrides && Object.keys(params.assistantOverrides).length > 0) {
      body.assistantOverrides = params.assistantOverrides;
    }
    const response = await api.post('/calls/', body);
    return response.data;
  },

  /** Get call by ID (for polling status, e.g. to detect when outbound call ended). */
  getCall: async (callId: string): Promise<{ success: boolean; data?: { id: string; status?: string; endedAt?: string } }> => {
    const response = await api.get(`/calls/${callId}`);
    return response.data;
  },

  updateFollowUpStatus: async (
    callSummaryId: string,
    status: 'pending' | 'completed' | 'not_required' | 'deferred',
    deferredUntil?: string
  ) => {
    const body: { status: string; deferred_until?: string } = { status };
    if (status === 'deferred' && deferredUntil) body.deferred_until = deferredUntil;
    const response = await api.patch(`/calls/${callSummaryId}/follow-up-status`, body);
    return response.data;
  },
};

export const followUpsAPI = {
  getFollowUps: async (): Promise<{ follow_ups: FollowUpListItem[] }> => {
    const response = await api.get('/analytics/follow-ups');
    return response.data;
  },
};

export interface FollowUpListItem {
  call_summary_id: string;
  lead_id: string;
  lead_name: string;
  lead_phone: string;
  lead_email: string | null;
  lead_temperature: string | null;
  disposition: string | null;
  run_id: string | null;
  run_name: string | null;
  follow_up_action: { action_required?: boolean; action_type?: string; description?: string; urgency?: string; deadline_mentioned?: string; notes?: string };
  follow_up_status: string | null;
  follow_up_resolved_at: string | null;
  follow_up_deferred_until: string | null;
  timestamp: string | null;
}

export const subscriptionAPI = {
  getUsage: async () => {
    const response = await api.get('/billing/usage');
    return response.data;
  },

  getCurrency: async (): Promise<{ currency: string }> => {
    const response = await api.get('/billing/currency');
    return response.data;
  },

  subscribe: async (planId: string) => {
    const response = await api.post('/billing/subscribe', buildSubscribePayload(planId));
    return response.data;
  },

  addCredits: async (credits: number) => {
    const response = await api.post('/billing/add-credits', buildAddCreditsPayload(credits));
    return response.data;
  },

  createCustomer: async () => {
    const response = await api.post('/billing/create-customer', buildCreateCustomerPayload());
    return response.data;
  },

  getSubscriptionLinks: async () => {
    const response = await api.get('/billing/subscription-links');
    return response.data;
  },

  getSubscriptionLink: async (planId: string) => {
    const response = await api.get(`/billing/subscription-link/${planId}`);
    return response.data;
  },

  getCreditLinks: async () => {
    const response = await api.get('/billing/credit-links');
    return response.data;
  },

  // Checkout sessions — region-aware.
  // India (REGION=in) hits /billing-in/* routes backed by RazorpayProvider (LT-109).
  // AU stays on /billing/* routes backed by StripeProvider.
  createSubscriptionCheckout: async (planId: string) => {
    const prefix = IS_INDIA ? '/billing-in' : '/billing';
    const response = await api.post(`${prefix}/create-subscription-checkout?plan_id=${planId}`);
    return response.data;
  },

  changePlan: async (planId: string) => {
    const prefix = IS_INDIA ? '/billing-in' : '/billing';
    const param = IS_INDIA ? 'new_plan_id' : 'plan_id';
    const response = await api.post(`${prefix}/change-plan?${param}=${planId}`);
    return response.data;
  },

  cancelPendingDowngrade: async () => {
    // India: cancel at period end via /billing-in/cancel (no "pending downgrade" model).
    if (IS_INDIA) {
      const response = await api.post('/billing-in/cancel');
      return response.data;
    }
    const response = await api.post('/billing/cancel-pending-downgrade');
    return response.data;
  },

  createCreditCheckout: async (packageId: string) => {
    const prefix = IS_INDIA ? '/billing-in' : '/billing';
    const response = await api.post(`${prefix}/create-credit-checkout?package_id=${packageId}`);
    return response.data;
  },

  createCustomerPortal: async () => {
    const prefix = IS_INDIA ? '/billing-in' : '/billing';
    const response = await api.post(`${prefix}/customer-portal`);
    return response.data;
  },

  getCredits: async () => {
    const response = await api.get('/billing/credits');
    return response.data;
  },

  // Enterprise "Contact Sales" lead capture. Region-agnostic — the /billing
  // router is mounted in both AU and IN deployments.
  submitEnterpriseEnquiry: async (payload: {
    name: string;
    email: string;
    company?: string;
    phone?: string;
    expected_volume?: string;
    message?: string;
  }): Promise<{ id: string; message: string }> => {
    const response = await api.post('/billing/enterprise-enquiry', payload);
    return response.data;
  },
};

// LT-538 — workspace credit-rate settings for client-side cost estimates.
export interface CreditRates {
  seconds_per_credit_normal: number;
  credits_per_sms: number;
  credits_per_email: number;
}

export const creditsAPI = {
  getBalance: async () => {
    const response = await api.get('/credits/balance');
    return response.data;
  },

  // LT-538 — credit rates for the campaign cost estimate (any auth'd user).
  getRates: async (): Promise<CreditRates> => {
    const response = await api.get('/credits/rates');
    return response.data;
  },

  getTransactions: async (skip: number = 0, limit: number = 50) => {
    const response = await api.get(`/credits/transactions?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  validateCredits: async (requiredCredits: number) => {
    const response = await api.get(`/credits/validate/${requiredCredits}`);
    return response.data;
  },

  reserveCredits: async (runId: string, leadId: string, amount: number = 1) => {
    const response = await api.post('/credits/reserve', {
      run_id: runId,
      lead_id: leadId,
      amount: amount
    });
    return response.data;
  },

  releaseCredits: async (runId: string, leadId: string) => {
    const response = await api.post('/credits/release', {
      run_id: runId,
      lead_id: leadId
    });
    return response.data;
  },

};

export const inboundCallsAPI = {
  getCalls: async (
    skip: number = 0, 
    limit: number = 50, 
    phoneFilter?: string, 
    runFilter?: string, 
    statusFilter?: string
  ): Promise<InboundCall[]> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      ...(phoneFilter && { phone_filter: phoneFilter }),
      ...(runFilter && { run_filter: runFilter }),
      ...(statusFilter && { status_filter: statusFilter })
    });
    
    const response = await api.get(`/inbound/calls?${params}`);
    return response.data;
  },

  getCall: async (callId: string): Promise<InboundCall> => {
    const response = await api.get(`/inbound/calls/${callId}`);
    return response.data;
  },

  getStats: async (days: number = 30): Promise<InboundCallStats> => {
    const response = await api.get(`/inbound/stats?days=${days}`);
    return response.data;
  },

  createCall: async (callData: InboundCallCreate): Promise<ApiResponse> => {
    const response = await api.post('/inbound/call', callData);
    return response.data;
  },

  getMatchingRules: async (): Promise<InboundCallMatchingRule[]> => {
    const response = await api.get('/inbound/matching-rules');
    return response.data;
  },

  createMatchingRule: async (ruleData: InboundCallMatchingRuleCreate): Promise<ApiResponse> => {
    const response = await api.post('/inbound/matching-rules', ruleData);
    return response.data;
  },
};

export const inboundConfigAPI = {
  getConfiguration: async (): Promise<any> => {
    const response = await api.get('/inbound-config/');
    return response.data;
  },

  createOrUpdateConfiguration: async (configData: any): Promise<any> => {
    const response = await api.post('/inbound-config/', configData);
    return response.data;
  },

  updateConfiguration: async (configData: any): Promise<any> => {
    const response = await api.put('/inbound-config/', configData);
    return response.data;
  },

  deleteConfiguration: async (): Promise<ApiResponse> => {
    const response = await api.delete('/inbound-config/');
    return response.data;
  },

};

export const notificationsAPI = {
  getList: async (unreadOnly: boolean = false, skip: number = 0, limit: number = 50) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      ...(unreadOnly && { unread_only: 'true' }),
    });
    const response = await api.get(`/notifications/?${params}`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  markRead: async (notificationId: string) => {
    const response = await api.post(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  delete: async (notificationId: string) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

// Custom Variables API
export const customVariablesAPI = {
  list: async (includeArchived = false) => {
    const response = await api.get('/custom-variables/', { params: { include_archived: includeArchived } });
    return response.data;
  },
  create: async (data: { name: string; variable_type: string; dropdown_options?: string[] }) => {
    const response = await api.post('/custom-variables/', data);
    return response.data;
  },
  get: async (variableId: string) => {
    const response = await api.get(`/custom-variables/${variableId}`);
    return response.data;
  },
  update: async (variableId: string, data: { name?: string; dropdown_options?: string[]; is_archived?: boolean }) => {
    const response = await api.put(`/custom-variables/${variableId}`, data);
    return response.data;
  },
  checkEligibility: async () => {
    const response = await api.get('/custom-variables/eligibility/check');
    return response.data;
  },
  getCampaignVariables: async (runId: string) => {
    const response = await api.get(`/custom-variables/campaign/${runId}`);
    return response.data;
  },
  attachToCampaign: async (runId: string, attachments: Array<{ variable_id: string; value?: string; sort_order?: number }>) => {
    const response = await api.post(`/custom-variables/campaign/${runId}`, attachments);
    return response.data;
  },
};

// SMS types
export interface SmsTemplate {
  id: string;
  name: string;
  body: string;
  is_active: boolean;
  created_at: string;
  // LT-238: India DLT/MSG91 fields. Always present in the response (may be null/empty on AU).
  dlt_template_id?: string | null;
  msg91_template_id?: string | null;
  sender_id?: string | null;
  // LT-238a: placeholder→canonical-field map, e.g. {"var1": "lead_name"}.
  variable_map?: Record<string, string> | null;
  // LT-507: non-null → booking-bearing template (only the Book-Meeting
  // step can pick it). Null → regular template.
  booking_page_id?: string | null;
}

export interface SmsTemplateWritePayload {
  name?: string;
  body?: string;
  dlt_template_id?: string | null;
  msg91_template_id?: string | null;
  sender_id?: string | null;
  variable_map?: Record<string, string> | null;
  // LT-507: include in update payload to change the booking binding.
  // ``null`` = regular template, UUID = booking-bearing.
  booking_page_id?: string | null;
  // LT-507: required when going non-null → different non-null on a
  // template that's in use by Book-Meeting steps. UI surfaces a confirm
  // dialog before sending this flag.
  confirm_change?: boolean;
}

export interface SmsSendResponse {
  success: boolean;
  message: string;
  twilio_sid?: string;
  credits_deducted: number;
  provider?: string | null;
  provider_message_id?: string | null;
}

export interface SmsBulkSendResponse {
  total: number;
  sent: number;
  failed: number;
  total_credits_deducted: number;
  results: { lead_id: string; success: boolean; message: string }[];
}

export const smsAPI = {
  getTemplates: async (
    opts?: { has_booking_page?: boolean },
  ): Promise<SmsTemplate[]> => {
    const params: Record<string, boolean> = {};
    if (typeof opts?.has_booking_page === 'boolean') {
      params.has_booking_page = opts.has_booking_page;
    }
    const response = await api.get('/sms/templates', { params });
    return response.data;
  },
  createTemplate: async (data: SmsTemplateWritePayload & { name: string; body: string; booking_page_id?: string | null }): Promise<SmsTemplate> => {
    const response = await api.post('/sms/templates', data);
    return response.data;
  },
  updateTemplate: async (id: string, data: SmsTemplateWritePayload): Promise<SmsTemplate> => {
    const response = await api.put(`/sms/templates/${id}`, data);
    return response.data;
  },
  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/sms/templates/${id}`);
  },
  sendSms: async (data: { lead_id: string; template_id: string; custom_body?: string }): Promise<SmsSendResponse> => {
    const response = await api.post('/sms/send', data);
    return response.data;
  },
  bulkSendSms: async (data: { lead_ids: string[]; template_id: string; custom_body?: string }): Promise<SmsBulkSendResponse> => {
    const response = await api.post('/sms/bulk-send', data);
    return response.data;
  },
};

// Email templates & outbound send (platform SendGrid)
export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  is_active: boolean;
  created_at: string;
  // LT-507: non-null → booking-bearing template (only the Book-Meeting
  // step can pick it). Null → regular template.
  booking_page_id?: string | null;
}

export interface EmailTemplateWritePayload {
  name?: string;
  subject?: string;
  body?: string;
  // LT-507: include in update payload to change the booking binding.
  booking_page_id?: string | null;
  // LT-507: required when going non-null → different non-null on a
  // template that's in use by Book-Meeting steps.
  confirm_change?: boolean;
}

export interface EmailSendResponse {
  success: boolean;
  message: string;
  credits_deducted: number;
  provider_message_id?: string | null;
}

export interface EmailBulkSendResultItem {
  lead_id: string;
  success: boolean;
  message: string;
  provider_message_id?: string | null;
}

export interface EmailBulkSendResponse {
  total: number;
  sent: number;
  failed: number;
  total_credits_deducted: number;
  results: EmailBulkSendResultItem[];
}

export const emailAPI = {
  getTemplates: async (
    opts?: { has_booking_page?: boolean },
  ): Promise<EmailTemplate[]> => {
    const params: Record<string, boolean> = {};
    if (typeof opts?.has_booking_page === 'boolean') {
      params.has_booking_page = opts.has_booking_page;
    }
    const response = await api.get('/email/templates', { params });
    return response.data;
  },
  createTemplate: async (
    data: { name: string; subject: string; body: string; booking_page_id?: string | null },
  ): Promise<EmailTemplate> => {
    const response = await api.post('/email/templates', data);
    return response.data;
  },
  updateTemplate: async (
    id: string,
    data: EmailTemplateWritePayload,
  ): Promise<EmailTemplate> => {
    const response = await api.put(`/email/templates/${id}`, data);
    return response.data;
  },
  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/email/templates/${id}`);
  },
  sendEmail: async (data: {
    lead_id: string;
    template_id: string;
    custom_subject?: string;
    custom_body?: string;
    sender_id?: string;  // LT-93
  }): Promise<EmailSendResponse> => {
    const response = await api.post('/email/send', data);
    return response.data;
  },
  bulkSendEmail: async (data: {
    lead_ids: string[];
    template_id: string;
    custom_subject?: string;
    custom_body?: string;
    sender_id?: string;
  }): Promise<EmailBulkSendResponse> => {
    const response = await api.post('/email/bulk-send', data);
    return response.data;
  },
};

// LT-93 — verified sender + platform default
export interface VerifiedSender {
  id: string;
  email: string;
  kind: string; // 'single_sender' for v1
  status: 'pending' | 'verified' | 'failed';
  sendgrid_sender_id?: string | null;
  created_at: string;
  updated_at: string;
  verified_at?: string | null;
}

export interface PlatformDefaultSender {
  email: string | null;
}

export const emailSendersAPI = {
  list: async (): Promise<VerifiedSender[]> => {
    const r = await api.get('/email/senders');
    return r.data;
  },
  platformDefault: async (): Promise<PlatformDefaultSender> => {
    const r = await api.get('/email/senders/platform-default');
    return r.data;
  },
  create: async (data: { email: string; nickname?: string; from_name?: string; reply_to?: string }): Promise<VerifiedSender> => {
    const r = await api.post('/email/senders', data);
    return r.data;
  },
  resend: async (id: string): Promise<void> => {
    await api.post(`/email/senders/${id}/resend`);
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/email/senders/${id}`);
  },
  refreshStatus: async (id: string): Promise<VerifiedSender> => {
    const r = await api.get(`/email/senders/${id}/status`);
    return r.data;
  },
};

// Single send accepts an optional sender_id — extend the existing emailAPI.sendEmail signature.
// (Done here as a re-export so the type system picks up the field; the runtime call already
// passes through anything in data via JSON body.)
export type EmailSendRequestExtended = {
  lead_id: string;
  template_id: string;
  custom_subject?: string;
  custom_body?: string;
  sender_id?: string;
};

// LT-550/551 — Campaign-from-Brief (upload path) extraction API.
export interface BriefClarification {
  id: string;
  question: string;
  suggested_default: string;
  category?: string | null;
}
export type BriefExtractionStatus = 'awaiting_clarification' | 'ready_for_review';
export interface BriefExtractionResult {
  campaign_id: string;
  agent_id: string;
  draft_campaign: Record<string, unknown>;
  draft_agent: Record<string, unknown>;
  clarifications: BriefClarification[];
  cycle: number;
  status: BriefExtractionStatus;
  // LT-553: tweaks left this launch session (null outside the tweak flow).
  tweaks_remaining?: number | null;
}
export const campaignBriefAPI = {
  // Upload a .docx brief → round-1 extraction (draft + clarifications).
  fromBrief: async (file: File): Promise<BriefExtractionResult> => {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post<BriefExtractionResult>('/campaigns/from-brief', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  // Submit answers to a round's clarifications → next round / ready_for_review.
  answerClarifications: async (
    campaignId: string,
    answers: Record<string, string>,
  ): Promise<BriefExtractionResult> => {
    const r = await api.post<BriefExtractionResult>(
      `/campaigns/${campaignId}/from-brief/answer-clarifications`,
      { answers },
    );
    return r.data;
  },
  // LT-553: apply one natural-language tweak → LLM regen + new agent version.
  applyTweak: async (campaignId: string, tweak: string): Promise<BriefExtractionResult> => {
    const r = await api.post<BriefExtractionResult>(
      `/campaigns/${campaignId}/from-brief/tweak`,
      { tweak },
    );
    return r.data;
  },
};

// LT-546 — workspace campaign-creation defaults. Both campaign-creation
// paths (wizard + upload) inherit these when the operator doesn't override.
export interface WorkspaceDialWindow {
  start: string; // HH:MM 24h
  end: string; // HH:MM 24h
  timezone: string; // IANA tz
  days: string[]; // ['mon','tue',...]
}
export interface WorkspaceRetryPolicy {
  retry_count: number; // retries AFTER the initial attempt; 1..5
  retry_delays_days: number[]; // positive ints; length === retry_count
}
export interface WorkspaceDefaults {
  voice_gender_default: 'male' | 'female' | null;
  voice_language_default: 'en' | 'en_hi' | 'en_id' | null;
  dial_window_default: WorkspaceDialWindow | null;
  retry_policy_default: WorkspaceRetryPolicy | null;
}
export type WorkspaceDefaultsUpdate = Partial<WorkspaceDefaults>;

// LT-554 — workspace default Agent. NULL until the first agent is created
// in the workspace. The wizard Persona step's "use workspace default agent"
// choice reads this; workspace settings changes it (admin-only).
export interface WorkspaceDefaultAgent {
  default_agent_id: string | null;
}

// Workspace + member + invite (LT-126 backend surface)
export const workspaceAPI = {
  getWorkspace: async (): Promise<Workspace> => {
    const r = await api.get<Workspace>('/workspaces/me');
    return r.data;
  },
  // LT-546: campaign-creation defaults. The backend coalesces NULLs to
  // region-sensible values on GET, so the returned fields are always usable.
  getDefaults: async (workspaceId: string): Promise<WorkspaceDefaults> => {
    const r = await api.get<WorkspaceDefaults>(`/workspaces/${workspaceId}/defaults`);
    return r.data;
  },
  updateDefaults: async (
    workspaceId: string,
    body: WorkspaceDefaultsUpdate,
  ): Promise<WorkspaceDefaults> => {
    const r = await api.put<WorkspaceDefaults>(`/workspaces/${workspaceId}/defaults`, body);
    return r.data;
  },
  // LT-554: the workspace default Agent (NULL until the first agent exists).
  getDefaultAgent: async (workspaceId: string): Promise<WorkspaceDefaultAgent> => {
    const r = await api.get<WorkspaceDefaultAgent>(`/workspaces/${workspaceId}/default-agent`);
    return r.data;
  },
  updateDefaultAgent: async (
    workspaceId: string,
    agentId: string,
  ): Promise<WorkspaceDefaultAgent> => {
    const r = await api.put<WorkspaceDefaultAgent>(
      `/workspaces/${workspaceId}/default-agent`,
      { default_agent_id: agentId },
    );
    return r.data;
  },
  renameWorkspace: async (name: string): Promise<Workspace> => {
    const r = await api.patch<Workspace>('/workspaces/me', { name });
    return r.data;
  },
  listMembers: async (): Promise<WorkspaceMember[]> => {
    const r = await api.get<WorkspaceMember[]>('/workspaces/members');
    return r.data;
  },
  changeMemberRole: async (userId: string, role: WorkspaceRole): Promise<WorkspaceMember> => {
    const r = await api.patch<WorkspaceMember>(`/workspaces/members/${userId}`, { role });
    return r.data;
  },
  removeMember: async (userId: string): Promise<void> => {
    await api.delete(`/workspaces/members/${userId}`);
  },
  listInvites: async (status: 'open' | 'all' = 'open'): Promise<WorkspaceInvite[]> => {
    const r = await api.get<WorkspaceInvite[]>('/workspaces/invites', { params: { status } });
    return r.data;
  },
  createInvite: async (email: string, role: WorkspaceRole): Promise<WorkspaceInvite> => {
    const r = await api.post<WorkspaceInvite>('/workspaces/invites', { email, role });
    return r.data;
  },
  revokeInvite: async (inviteId: string): Promise<void> => {
    await api.delete(`/workspaces/invites/${inviteId}`);
  },
  resendInvite: async (inviteId: string): Promise<WorkspaceInvite> => {
    const r = await api.post<WorkspaceInvite>(`/workspaces/invites/${inviteId}/resend`);
    return r.data;
  },
  lookupInvite: async (token: string): Promise<InviteLookupResponse> => {
    const r = await api.get<InviteLookupResponse>(`/workspaces/invites/lookup/${encodeURIComponent(token)}`);
    return r.data;
  },
  acceptInvite: async (payload: InviteAcceptRequest): Promise<InviteAcceptResponse> => {
    const r = await api.post<InviteAcceptResponse>('/workspaces/invites/accept', payload);
    return r.data;
  },
};

// LT-175 — email deliverability admin (suppression CRUD + webhook health).
export interface EmailSuppression {
  id: string;
  email: string;
  reason: string;
  suppressed_at: string;
}

export interface EmailWebhookHealth {
  last_event_received_at: string | null;
  events_last_24h: number;
}

export const emailAdminAPI = {
  listSuppressions: async (
    params: { search?: string; limit?: number; offset?: number } = {}
  ): Promise<EmailSuppression[]> => {
    const r = await api.get<EmailSuppression[]>('/email-suppressions', { params });
    return r.data;
  },
  addSuppression: async (email: string): Promise<EmailSuppression> => {
    const r = await api.post<EmailSuppression>('/email-suppressions', { email });
    return r.data;
  },
  removeSuppression: async (id: string): Promise<void> => {
    await api.delete(`/email-suppressions/${id}`);
  },
  getWebhookHealth: async (): Promise<EmailWebhookHealth> => {
    const r = await api.get<EmailWebhookHealth>('/email-suppressions/webhook-health');
    return r.data;
  },
};

/** Extract the stable `detail.code` from an API error (LT-126+ error shape). */
export function extractErrorCode(err: any): string | null {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    return detail.code;
  }
  return null;
}

// ---------------------------------------------------------------------------
// LT-502 — call-captured referral → new Contact conversion.
// ---------------------------------------------------------------------------

export interface ContactFromReferralResponse {
  contact_id: string;
  created: boolean;
}

/** Convert a captured referral on a CallSummary into a new Contact.
 *
 * If the workspace already has a Contact with the referral phone, the
 * response carries that contact's id with ``created=false`` instead of
 * inserting a duplicate.
 */
export async function createContactFromReferral(
  callSummaryId: string,
): Promise<ContactFromReferralResponse> {
  const r = await api.post<ContactFromReferralResponse>(
    '/contacts/from-referral',
    { call_summary_id: callSummaryId },
  );
  return r.data;
}

export default api;
